import React from 'react'

import { InvestmentProduct, PortfolioSecurityAllocation } from 'generated/types'
import { getFormattedDecimalPercentage } from 'pages/invest/SharedFunctions'
import { PORTFOLIO_DESCRIPTION } from 'utils/testing/testIDs'

import {
  Header,
  HeadingStyled,
  PurpleBadge,
  StyledHeaderBodyText,
} from './styled-components'
import { PortfolioInvestmentProduct } from './types'

export const displayedPercentage = (value: number) => `${Math.round(value)}%`
export const decimalPercentage = (value: number) => value / 100

export const getCurrentProductPortfolio = (location: {
  pathname: string
}): PortfolioInvestmentProduct => {
  let currentProductPortfolio = PortfolioInvestmentProduct.Invest

  if (location?.pathname.includes('core')) {
    currentProductPortfolio = PortfolioInvestmentProduct.Invest
  } else if (location?.pathname.includes('later')) {
    currentProductPortfolio = PortfolioInvestmentProduct.Later
  } else if (location?.pathname.includes('early')) {
    currentProductPortfolio = PortfolioInvestmentProduct.Early
  }

  return currentProductPortfolio
}

export const getStockBondPercentagesWithSecurityPercentages = (
  securityAllocations: PortfolioSecurityAllocation[],
  securityPercentages: number[],
  allocationPercent: number,
) => {
  let totalBondsPercentage = 0
  let totalStocksPercentage = 0
  let totalCryptoPercentage = 0

  securityAllocations.forEach(
    (allocation: PortfolioSecurityAllocation, index: number) => {
      if (allocation?.investmentCategories?.bond === 100) {
        totalBondsPercentage += securityPercentages[index]
      } else if ((allocation?.investmentCategories as any)?.crypto === 100) {
        totalCryptoPercentage += securityPercentages[index]
      } else {
        totalStocksPercentage += securityPercentages[index]
      }
    },
  )

  if (allocationPercent < 100) {
    totalStocksPercentage += (100 - allocationPercent) / 100
  }

  const bondEtfPercent = getFormattedDecimalPercentage(totalBondsPercentage)
  const stockEtfPercent = getFormattedDecimalPercentage(totalStocksPercentage)
  const cryptoEtfPercent = getFormattedDecimalPercentage(totalCryptoPercentage)
  const basePercent = getFormattedDecimalPercentage(allocationPercent, false)
  const customPercent = getFormattedDecimalPercentage(
    (100 - allocationPercent) / 100,
  )
  return {
    bondEtfPercent,
    stockEtfPercent,
    cryptoEtfPercent,
    basePercent,
    customPercent,
  }
}

export const getInvestmentCategoryPercentages = (
  portfolioSecurityAllocations: PortfolioSecurityAllocation[] | undefined,
) => {
  let totalBondsPercentage = 0
  let totalStocksPercentage = 0
  let totalCryptoPercentage = 0

  portfolioSecurityAllocations?.forEach(
    (security: PortfolioSecurityAllocation) => {
      if (security?.investmentCategories?.bond === 100) {
        totalBondsPercentage += security?.allocationPercent ?? 0
      } else if (security?.investmentCategories?.crypto === 100) {
        totalCryptoPercentage += security?.allocationPercent ?? 0
      } else {
        totalStocksPercentage += security?.allocationPercent ?? 0
      }
    },
  )

  return {
    bondEtfPercent: getFormattedDecimalPercentage(totalBondsPercentage, false),
    stockEtfPercent: getFormattedDecimalPercentage(
      totalStocksPercentage,
      false,
    ),
    cryptoEtfPercent: getFormattedDecimalPercentage(
      totalCryptoPercentage,
      false,
    ),
  }
}

export const renderHeader = (
  isProductInvest: boolean,
  portfolioRisk: string,
  portfolioDescription: string | JSX.Element,
) => (
  <Header data-testid={PORTFOLIO_DESCRIPTION}>
    {!isProductInvest && <PurpleBadge>Age-Based</PurpleBadge>}
    <HeadingStyled>{portfolioRisk}</HeadingStyled>
    <StyledHeaderBodyText>{portfolioDescription}</StyledHeaderBodyText>
  </Header>
)

export const investmentProductMap = {
  [PortfolioInvestmentProduct.Invest]: InvestmentProduct.Invest,
  [PortfolioInvestmentProduct.Later]: InvestmentProduct.Later,
  [PortfolioInvestmentProduct.Early]: InvestmentProduct.Early,
}

export const calculateSecurityPercentagesNoNormalization = (
  portfolioSecurities: PortfolioSecurityAllocation[] = [],
  portfolioAllocationPercent = 0,
) => {
  return portfolioSecurities.map((security: PortfolioSecurityAllocation) => {
    return (
      (security.allocationPercent / 100) * (portfolioAllocationPercent / 100)
    )
  })
}

// The following algorithm is being used across all three clients to do the base security calculations.
// Reference: https://revs.runtime-revolution.com/getting-100-with-rounded-percentages-273ffa70252b
export const calculateSecurityPercentages = (
  portfolioSecurities: PortfolioSecurityAllocation[] = [],
  portfolioAllocationPercent = 0,
) => {
  const normalizedSecurities = portfolioSecurities.map((security) => {
    return {
      ...security,
      allocationPercent:
        decimalPercentage(security.allocationPercent) *
        portfolioAllocationPercent,
    }
  })

  let calculatedSum = 0
  let roundedSum = 0

  for (const security of normalizedSecurities) {
    const percentage = security.allocationPercent

    calculatedSum += percentage
    roundedSum += Math.floor(percentage)
  }

  const difference = calculatedSum - roundedSum

  const greatestDecimalSecurities: string[] = []

  const sortedSecurities = [...normalizedSecurities]

  sortedSecurities
    .sort((security1, security2) => {
      const allocationPercent1 = security1.allocationPercent
      const allocationPercent2 = security2.allocationPercent

      const percent1DecimalPart =
        allocationPercent1 - Math.floor(allocationPercent1)

      const percent2Decimalpart =
        allocationPercent2 - Math.floor(allocationPercent2)

      return percent2Decimalpart - percent1DecimalPart
    })
    .forEach((security, index) => {
      if (index < difference) {
        greatestDecimalSecurities.push(security.symbol)
      }
    })

  // Distribute the difference to the numbers with the highest decimal point values.
  const updatedSecurityPercentages = normalizedSecurities.map((security) => {
    let updatedAllocationPercent = security.allocationPercent

    if (greatestDecimalSecurities.includes(security.symbol)) {
      updatedAllocationPercent = Math.floor(security.allocationPercent) + 1
    } else {
      updatedAllocationPercent = Math.floor(security.allocationPercent)
    }

    return updatedAllocationPercent / 100
  })

  return updatedSecurityPercentages
}

export const orderPortfolioSecurityAllocations = (
  portfolioSecurityAllocations: PortfolioSecurityAllocation[],
) => {
  const bondSecurities =
    portfolioSecurityAllocations?.filter(
      (security) => security?.investmentCategories?.bond === 100,
    ) || []
  const equitySecurities =
    portfolioSecurityAllocations?.filter(
      (security) => security?.investmentCategories?.equity === 100,
    ) || []
  const cryptoSecurities =
    portfolioSecurityAllocations?.filter(
      (security) => security?.investmentCategories?.crypto === 100,
    ) || []
  const otherSecurities =
    portfolioSecurityAllocations?.filter((security) => {
      return (
        security?.investmentCategories?.crypto !== 100 &&
        security?.investmentCategories?.equity !== 100 &&
        security?.investmentCategories?.bond !== 100
      )
    }) || []
  return [
    ...equitySecurities,
    ...bondSecurities,
    ...cryptoSecurities,
    ...otherSecurities,
  ]
}

export const orderSecurities = (
  portfolioSecurityAllocations: PortfolioSecurityAllocation[],
) => {
  const bondSecurities =
    portfolioSecurityAllocations?.filter(
      (security) => security?.investmentCategories?.bond === 100,
    ) || []
  const equitySecurities =
    portfolioSecurityAllocations?.filter(
      (security) => security?.investmentCategories?.equity === 100,
    ) || []
  const cryptoSecurities =
    portfolioSecurityAllocations?.filter(
      (security) => security?.investmentCategories?.crypto === 100,
    ) || []
  const otherSecurities =
    portfolioSecurityAllocations?.filter((security) => {
      return (
        security?.investmentCategories?.crypto !== 100 &&
        security?.investmentCategories?.equity !== 100 &&
        security?.investmentCategories?.bond !== 100
      )
    }) || []
  return [
    ...equitySecurities,
    ...bondSecurities,
    ...cryptoSecurities,
    ...otherSecurities,
  ]
}
